// contexts/OrgContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import BASE_URL from "../config";
import { useAuthData } from "./AuthDataContext";

const OrgContext = createContext();

export const OrgProvider = ({ children }) => {
  const { userData, loading: authLoading } = useAuthData();
  
  // Initialize from localStorage to persist across refreshes
  const [selectedOrgId, setSelectedOrgId] = useState(
    () => localStorage.getItem("selectedOrgId") || ""
  );
  const [selectedOrgName, setSelectedOrgName] = useState(
    () => localStorage.getItem("selectedOrgName") || ""
  );
  const [allOrganisations, setAllOrganisations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);

  // Derive isSuperAdmin from shared userData
  const isSuperAdmin = userData?.isSuperAdmin || false;

  // Effect to sync organization data when userData changes
  useEffect(() => {
    if (authLoading) return;
    
    // No user data means not logged in
    if (!userData) {
      localStorage.removeItem("selectedOrgId");
      localStorage.removeItem("selectedOrgName");
      setSelectedOrgId("");
      setSelectedOrgName("");
      setAllOrganisations([]);
      setOrgLoading(false);
      return;
    }

    const initOrg = async () => {
      try {
        const userOrgId = userData.organisation_id;
        const userOrgName = userData.organisation_name;
        const superAdminStatus = userData.isSuperAdmin || false;

        // For super admin, fetch all organizations for the switcher
        let allOrgs = [];
        console.log("[OrgContext] superAdminStatus:", superAdminStatus, "isSuperAdmin field:", userData.isSuperAdmin);
        if (superAdminStatus) {
          try {
            let firebaseUser = getAuth().currentUser;
            console.log("[OrgContext] getAuth().currentUser:", firebaseUser?.email ?? null);
            if (!firebaseUser) {
              firebaseUser = await new Promise((resolve) => {
                const unsub = onAuthStateChanged(getAuth(), (u) => { unsub(); resolve(u); });
              });
              console.log("[OrgContext] onAuthStateChanged resolved:", firebaseUser?.email ?? null);
            }
            if (firebaseUser) {
              const token = await firebaseUser.getIdToken();
              console.log("[OrgContext] Fetching /api/organisations...");
              const allOrgsRes = await axios.get(`${BASE_URL}/api/organisations`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              allOrgs = allOrgsRes.data || [];
              console.log("[OrgContext] Fetched", allOrgs.length, "organisations");
              setAllOrganisations(allOrgs);
            } else {
              console.error("[OrgContext] No Firebase user — cannot fetch organisations");
            }
          } catch (err) {
            console.error("[OrgContext] Error fetching organisations:", err);
          }
        }

        // Set selected org after fetching
        const storedOrgId = localStorage.getItem("selectedOrgId");

        if (superAdminStatus && allOrgs.length > 0) {
          // Super admin: restore stored selection or default to first org
          // Use String() for type-safe comparison (localStorage is always string, org.id may be number)
          const storedOrgExists = storedOrgId
            ? allOrgs.find(org => String(org.id) === String(storedOrgId))
            : null;
          const orgToSelect = storedOrgExists || allOrgs[0];
          localStorage.setItem("selectedOrgId", String(orgToSelect.id));
          localStorage.setItem("selectedOrgName", orgToSelect.name || "");
          setSelectedOrgId(String(orgToSelect.id));
          setSelectedOrgName(orgToSelect.name || "");
        } else if (userOrgId) {
          // Regular user: use their assigned org
          localStorage.setItem("selectedOrgId", userOrgId);
          localStorage.setItem("selectedOrgName", userOrgName || "");
          setSelectedOrgId(userOrgId);
          setSelectedOrgName(userOrgName || "");
        }
      } finally {
        setOrgLoading(false);
      }
    };

    initOrg();
  }, [userData, authLoading]);

  const updateSelectedOrg = (id, name = "") => {
    localStorage.setItem("selectedOrgId", id);
    localStorage.setItem("selectedOrgName", name);
    setSelectedOrgId(id);
    setSelectedOrgName(name);
  };

  // Refresh organizations list (useful after creating new org)
  const refreshOrganisations = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const allOrgsRes = await axios.get(`${BASE_URL}/api/organisations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllOrganisations(allOrgsRes.data || []);
    } catch (err) {
      console.warn("Could not refresh organisations:", err);
    }
  };

  return (
    <OrgContext.Provider
      value={{ 
        selectedOrgId, 
        selectedOrgName, 
        updateSelectedOrg, 
        loading: authLoading || orgLoading,
        allOrganisations,
        isSuperAdmin,
        refreshOrganisations
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => useContext(OrgContext);
